import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { HeartHandshake, Stethoscope, ClipboardList, Phone, MessageCircle, ArrowDownRight, MapPin } from "lucide-react";
import { CLINIC, IMAGES } from "../../data/content";
import { scrollToId } from "../../lib/scroll";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay }) => (
    <span className="mask-line pb-1">
        <motion.span
            className="block"
            initial={{ y: "112%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.1, delay, ease: EASE }}
        >
            {children}
        </motion.span>
    </span>
);

const trust = [
    { icon: HeartHandshake, label: "Patient-Focused Care" },
    { icon: Stethoscope, label: "Comprehensive Dental Services" },
    { icon: ClipboardList, label: "Personalized Consultation" },
];

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
    const cardY = useTransform(scrollYProgress, [0, 1], [0, -60]);

    return (
        <section id="home" ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-28 lg:pt-32">
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -right-40 -top-40 h-[42rem] w-[42rem] rounded-full bg-[radial-gradient(circle,rgba(13,148,136,0.14),transparent_65%)]"
            />
            <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 pb-20 lg:grid-cols-[1.05fr_0.95fr] lg:px-10 lg:pb-28">
                <div>
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
                        className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-teal-accent"
                    >
                        <span className="h-px w-10 bg-teal-600/40" />
                        {CLINIC.name} · Kasba, Kolkata
                    </motion.p>

                    <h1 className="mt-6 font-display text-[2.9rem] font-light leading-[1.04] text-ink sm:text-6xl lg:text-[4.6rem]">
                        <MaskedLine delay={0.25}>Healthy <em className="font-medium text-teal-accent">Smiles</em></MaskedLine>
                        <MaskedLine delay={0.37}>Start With Better</MaskedLine>
                        <MaskedLine delay={0.49}>Dental Care.</MaskedLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
                        className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg"
                    >
                        Professional and patient-focused dental care for healthier teeth, confident smiles and better
                        oral health in Kasba, Kolkata.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
                        className="mt-9 flex flex-wrap items-center gap-4"
                    >
                        <button
                            data-testid="hero-book-appointment-btn"
                            onClick={() => scrollToId("#appointment")}
                            className="group inline-flex items-center gap-2 rounded-full bg-ink px-7 py-4 text-sm font-bold text-[#f8f6f0] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-teal-700"
                        >
                            Book Appointment
                            <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
                        </button>
                        <button
                            data-testid="hero-whatsapp-btn"
                            className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-7 py-4 text-sm font-bold text-ink transition-[border-color,background-color,transform] duration-300 hover:-translate-y-0.5 hover:border-teal-600 hover:bg-mist"
                        >
                            <MessageCircle className="h-4 w-4 text-teal-accent" />
                            WhatsApp Us
                        </button>
                        <a
                            data-testid="hero-call-link"
                            href={`tel:${CLINIC.phoneTel}`}
                            className="inline-flex items-center gap-2 text-sm font-bold text-ink/70 underline decoration-teal-500/50 decoration-2 underline-offset-4 transition-colors hover:text-ink"
                        >
                            <Phone className="h-4 w-4" />
                            Call Clinic
                        </a>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 1.05 }}
                        className="mt-12 grid max-w-xl grid-cols-1 gap-3 sm:grid-cols-3"
                    >
                        {trust.map((t, i) => (
                            <div
                                key={t.label}
                                data-testid={`hero-trust-${i}`}
                                className="flex items-center gap-3 rounded-2xl border border-ink/10 bg-white/70 px-4 py-3.5 backdrop-blur-sm"
                            >
                                <t.icon className="h-5 w-5 shrink-0 text-teal-accent" />
                                <span className="text-xs font-bold leading-snug text-ink/80">{t.label}</span>
                            </div>
                        ))}
                    </motion.div>
                </div>

                <div className="relative">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.94 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1.3, delay: 0.4, ease: EASE }}
                        className="relative mx-auto aspect-[4/5] w-full max-w-md overflow-hidden rounded-b-[2rem] rounded-t-[999px] border-[10px] border-white shadow-[0_40px_80px_-20px_rgba(11,31,42,0.35)]"
                    >
                        <motion.img
                            src={IMAGES.hero}
                            alt="Dentist providing gentle dental care to a patient"
                            style={{ y: imgY, scale: 1.15 }}
                            className="h-full w-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-ink/25 via-transparent to-transparent" />
                    </motion.div>

                    <motion.div
                        data-testid="hero-floating-card"
                        style={{ y: cardY }}
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 1, ease: EASE }}
                        className="float-soft absolute -left-2 bottom-10 rounded-2xl border border-white/50 bg-white/80 p-4 shadow-[0_20px_50px_-15px_rgba(11,31,42,0.3)] backdrop-blur-xl sm:-left-8"
                    >
                        <div className="flex items-center gap-3">
                            <span className="grid h-11 w-11 place-items-center rounded-full bg-teal-accent text-white">
                                <MapPin className="h-5 w-5" />
                            </span>
                            <div>
                                <p className="text-sm font-extrabold text-ink">Kasba, Kolkata</p>
                                <p className="text-xs font-semibold text-slate-500">Picnic Garden Rd</p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
